import {
  parseLocaleNumber,
  type DocumentProcessingClaim,
  type DocumentProcessingRun,
} from "./documentProcessingRun";

/**
 * The values a document's processing run found: measured results such as
 * "AMH 2.1 ng/mL", read from the run's claims so the patient can confirm
 * them before anything is saved.
 *
 * Saving goes through the run itself. A value the promotion writer can file
 * as the patient saw it is accepted and promoted; anything else (a value the
 * patient corrected, a value whose name or unit came from a neighbouring
 * claim, a figure kind the writer does not file) is recorded as a
 * patient-entered observation linked to the document, and its claim is
 * rejected so the run never files it a second time.
 *
 * The capability rules below mirror dashboard-api's
 * `document-promotion-capabilities.ts`. They decide which proposals a value
 * lands in, so they must move with that file.
 */

type ClaimStatus = DocumentProcessingClaim["status"];

/** The promotion capabilities a found value can be filed through. */
export type DocumentValueCapability = "clinical_result" | "semen_analysis";

/** How a value reaches the record when the patient confirms it. */
export type DocumentValueRoute = DocumentValueCapability | "record";

/** A value the reader found in a document. */
export type DocumentFoundValue = {
  /** The claim that carries the figure. */
  claimId: string;
  name: string;
  /** The figure as a plain decimal ("2.1"), read the way the document wrote it. */
  value: string;
  unit: string;
  /** YYYY-MM-DD, or null when neither the value nor the document has one. */
  date: string | null;
  /** Already accepted: saved, or on its way to the record. */
  saved: boolean;
  route: DocumentValueRoute;
};

/** A value as the patient leaves it: corrected, or added by hand. */
export type DocumentValueDraft = {
  name: string;
  value: string;
  unit: string;
  date: string | null;
};

export type DocumentClaimReview = {
  claimId: string;
  expectedVersion: number;
  decision: "accept" | "reject";
  reason: string;
  normalizedValue?: Record<string, unknown>;
};

export type DocumentValueConfirmationPlan = {
  reviews: DocumentClaimReview[];
  /** Capabilities whose accepted claims are promoted after the review. */
  capabilities: DocumentValueCapability[];
  /** Claims this confirmation accepts, so only their proposals are promoted. */
  acceptedClaimIds: string[];
  /** Values written as patient-entered observations linked to the document. */
  record: DocumentValueDraft[];
};

const REVIEWABLE_STATUSES = new Set<ClaimStatus>([
  "proposed",
  "ambiguous",
  "unsupported",
]);

const ELIGIBLE_STATUSES = new Set<ClaimStatus>([
  ...REVIEWABLE_STATUSES,
  "accepted",
]);

// dashboard-api `definitionsForClaim`: the clinical-result capability takes
// these classifications and these field paths.
const CLINICAL_RESULT_CLASSIFICATIONS = new Set([
  "lab_result",
  "imaging_result",
  "medical_history",
]);
const CLINICAL_RESULT_PATH = /lab|result|imaging|ultrasound|genetic|semen/i;

// dashboard-api `documentLooksLikeSemenAnalysis` and `isSemenAnalysisClaim`.
const SEMEN_DOCUMENT_NAME =
  /\b(semen|sperm|spermogramme|spermiogram|spermocytogram)/i;
const SEMEN_DOCUMENT_COMPONENT =
  /concentration|motilit|mobilit|morpholog|volume|vitalit|numeration|nombre[_. ]?total/i;
const SEMEN_CLAIM =
  /result|analys|sperm|semen|concentration|motilit|mobilit|morpholog|volume|vitalit|nombre|numeration|abstinence|recueil|prelevement/i;
const SEMEN_CLAIM_KINDS = new Set<DocumentProcessingClaim["kind"]>([
  "number",
  "amount",
  "text",
  "date",
  "identifier",
  "boolean",
]);

/**
 * Documents that report measured values. A prescription's dose or an
 * invoice's amount is a number too, but never a result.
 */
const VALUE_CLASSIFICATIONS = new Set([
  "lab_result",
  "imaging_result",
  "medical_history",
  "unknown",
  "other",
]);

/** Field paths a measured result sits under, on a document of any kind. */
const VALUE_PATH =
  /lab|result|imaging|ultrasound|genetic|semen|sperm|marker|test|analys|hormone|panel|blood|serum|follic|endometri/i;

/** Classifications whose every measured figure is a result. */
const RESULT_CLASSIFICATIONS = new Set(["lab_result", "imaging_result"]);

/** Where a report states things about itself rather than a result. */
const NON_VALUE_ROOTS = new Set([
  "document",
  "patient",
  "provider",
  "prescriber",
  "physician",
  "clinic",
  "facility",
  "laboratory",
  "metadata",
  "header",
  "footer",
  "page",
  "pages",
  "order",
  "accession",
  "insurance",
  "billing",
  "payment",
  "invoice",
  "identifier",
  "identifiers",
]);
const NON_VALUE_LEAVES = new Set([
  "id",
  "identifier",
  "number",
  "no",
  "phone",
  "fax",
  "zip",
  "postalcode",
  "age",
  "page",
  "pages",
  "pagecount",
  "year",
  "accessionnumber",
  "orderid",
  "sampleid",
  "specimenid",
]);

/** Leaves and segments that hold a reference range, not a result. */
const REFERENCE_SEGMENTS = new Set([
  "ref",
  "reference",
  "references",
  "range",
  "ranges",
  "referencerange",
  "refrange",
  "normalrange",
  "normalvalues",
  "interval",
  "referenceinterval",
  "low",
  "high",
  "min",
  "max",
  "lower",
  "upper",
  "lowerlimit",
  "upperlimit",
  "referencelow",
  "referencehigh",
  "reflow",
  "refhigh",
]);
const REFERENCE_LABEL =
  /(^|\s)(ref\.?|reference|range|normal range|valeurs? de r[ée]f[ée]rence|norme)(\s|$)/i;

/** A label that names the column, not the test ("Value", "Résultat"). */
const GENERIC_LABELS = new Set([
  "",
  "value",
  "values",
  "result",
  "results",
  "resultat",
  "resultats",
  "valeur",
  "valeurs",
  "measurement",
  "mesure",
  "reading",
  "level",
  "taux",
]);

const NAME_LEAVES = new Set([
  "name",
  "marker",
  "markername",
  "test",
  "testname",
  "analyte",
  "label",
  "parameter",
  "examen",
  "analyse",
  "biomarker",
]);

const UNIT_LEAVES = new Set(["unit", "units", "unite"]);

function compact(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function segmentsOf(fieldPath: string): string[] {
  return fieldPath
    .split(".")
    .map((segment) => segment.replace(/\[\d+\]/g, ""))
    .filter(Boolean);
}

function leafOf(fieldPath: string): string {
  return segmentsOf(fieldPath).pop() ?? "";
}

function parentOf(fieldPath: string): string {
  const index = fieldPath.lastIndexOf(".");
  return index === -1 ? "" : fieldPath.slice(0, index);
}

function canonicalDate(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? "";
  return /^\d{4}-\d{2}-\d{2}$/.test(trimmed) &&
    !trimmed.startsWith("0000") &&
    !trimmed.startsWith("0001")
    ? trimmed
    : null;
}

/** "volume_ejaculat" → "Volume ejaculat"; "freeT4" → "Free t4". */
function humanize(segment: string): string {
  const words = segment
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : "";
}

function isEligible(claim: DocumentProcessingClaim): boolean {
  return ELIGIBLE_STATUSES.has(claim.status);
}

function isReferenceBound(claim: DocumentProcessingClaim): boolean {
  if (
    segmentsOf(claim.fieldPath).some((s) => REFERENCE_SEGMENTS.has(compact(s)))
  ) {
    return true;
  }
  return REFERENCE_LABEL.test(claim.rawLabel.trim());
}

/**
 * Whether the backend files this run's claims as a semen analysis. Decided
 * over the whole claim set, as dashboard-api does.
 */
export function isSemenAnalysisRun(run: DocumentProcessingRun): boolean {
  if (
    run.classificationCode !== "lab_result" &&
    run.classificationCode !== "medical_history"
  ) {
    return false;
  }
  const text = run.claims
    .filter(isEligible)
    .map((claim) => `${claim.fieldPath} ${claim.rawLabel} ${claim.rawValue}`)
    .join(" ")
    .toLowerCase();
  return SEMEN_DOCUMENT_NAME.test(text) && SEMEN_DOCUMENT_COMPONENT.test(text);
}

function isSemenGroupClaim(claim: DocumentProcessingClaim): boolean {
  return (
    SEMEN_CLAIM_KINDS.has(claim.kind) &&
    SEMEN_CLAIM.test(`${claim.fieldPath} ${claim.rawLabel}`)
  );
}

function readsAsValueDocument(run: DocumentProcessingRun): boolean {
  return (
    run.classificationCode === null ||
    VALUE_CLASSIFICATIONS.has(run.classificationCode)
  );
}

function isMeasuredValueClaim(
  run: DocumentProcessingRun,
  claim: DocumentProcessingClaim,
  semenRun: boolean,
): boolean {
  if (claim.kind !== "number" && claim.kind !== "amount") return false;
  if (parseLocaleNumber(claim.rawValue) === null) return false;
  if (isReferenceBound(claim)) return false;
  if (semenRun && isSemenGroupClaim(claim)) return true;
  const segments = segmentsOf(claim.fieldPath);
  if (NON_VALUE_ROOTS.has(compact(segments[0] ?? ""))) return false;
  if (NON_VALUE_LEAVES.has(compact(segments[segments.length - 1] ?? ""))) {
    return false;
  }
  if (
    run.classificationCode !== null &&
    RESULT_CLASSIFICATIONS.has(run.classificationCode)
  ) {
    return true;
  }
  return VALUE_PATH.test(claim.fieldPath) || VALUE_PATH.test(claim.rawLabel);
}

/** The first clean date the run states for the whole document. */
export function documentProcessingDate(
  run: DocumentProcessingRun,
): string | null {
  for (const claim of run.claims) {
    if (claim.kind !== "date" || claim.anomalies.length > 0) continue;
    const date = canonicalDate(claim.rawDate) ?? canonicalDate(claim.rawValue);
    if (date) return date;
  }
  return null;
}

function siblingsOf(
  run: DocumentProcessingRun,
  claim: DocumentProcessingClaim,
): DocumentProcessingClaim[] {
  const parent = parentOf(claim.fieldPath);
  if (!parent) return [];
  return run.claims.filter(
    (candidate) =>
      candidate.id !== claim.id && parentOf(candidate.fieldPath) === parent,
  );
}

function valueName(
  run: DocumentProcessingRun,
  claim: DocumentProcessingClaim,
): { name: string; fromLabel: boolean } {
  const label = claim.rawLabel.trim();
  if (!GENERIC_LABELS.has(compact(label))) {
    return { name: label, fromLabel: true };
  }
  const nameClaim = siblingsOf(run, claim).find(
    (sibling) =>
      NAME_LEAVES.has(compact(leafOf(sibling.fieldPath))) &&
      sibling.rawValue.trim().length > 0,
  );
  if (nameClaim) return { name: nameClaim.rawValue.trim(), fromLabel: false };
  const segments = segmentsOf(claim.fieldPath);
  const parentSegment = segments[segments.length - 2];
  const fromPath = humanize(parentSegment ?? segments[0] ?? "");
  return { name: fromPath || label, fromLabel: false };
}

function valueUnit(
  run: DocumentProcessingRun,
  claim: DocumentProcessingClaim,
): string {
  const own = claim.rawUnit?.trim() ?? "";
  if (own) return own;
  const unitClaim = siblingsOf(run, claim).find((sibling) =>
    UNIT_LEAVES.has(compact(leafOf(sibling.fieldPath))),
  );
  return unitClaim?.rawValue.trim() ?? "";
}

function valueDate(
  run: DocumentProcessingRun,
  claim: DocumentProcessingClaim,
  fallbackDate: string | null,
): string | null {
  const own = canonicalDate(claim.rawDate);
  if (own) return own;
  const dateClaim = siblingsOf(run, claim).find(
    (sibling) =>
      sibling.kind === "date" &&
      (canonicalDate(sibling.rawDate) ?? canonicalDate(sibling.rawValue)),
  );
  return (
    canonicalDate(dateClaim?.rawDate) ??
    canonicalDate(dateClaim?.rawValue) ??
    fallbackDate
  );
}

function routeFor(
  run: DocumentProcessingRun,
  claim: DocumentProcessingClaim,
  semenRun: boolean,
  nameFromLabel: boolean,
): DocumentValueRoute {
  if (semenRun && isSemenGroupClaim(claim)) return "semen_analysis";
  // The clinical-result writer files a claim's own label and unit, and only a
  // `number` claim with a unit becomes a measured quantity. Anything else is
  // recorded as the patient sees it.
  if (
    claim.kind === "number" &&
    CLINICAL_RESULT_PATH.test(claim.fieldPath) &&
    (run.classificationCode === null ||
      CLINICAL_RESULT_CLASSIFICATIONS.has(run.classificationCode)) &&
    nameFromLabel &&
    (claim.rawUnit?.trim() ?? "").length > 0
  ) {
    return "clinical_result";
  }
  return "record";
}

/**
 * The values a run found, in the document's order. Rejected and superseded
 * claims are left out; accepted ones come back marked as saved.
 */
export function findDocumentValues(
  run: DocumentProcessingRun,
  documentDate: string | null,
): DocumentFoundValue[] {
  if (run.status !== "succeeded" || !readsAsValueDocument(run)) return [];
  const semenRun = isSemenAnalysisRun(run);
  const fallbackDate = documentProcessingDate(run) ?? documentDate;
  return run.claims
    .filter(
      (claim) =>
        isEligible(claim) && isMeasuredValueClaim(run, claim, semenRun),
    )
    .slice()
    .sort((left, right) => left.ordinal - right.ordinal)
    .map((claim) => {
      const { name, fromLabel } = valueName(run, claim);
      const parsed = parseLocaleNumber(claim.rawValue);
      return {
        claimId: claim.id,
        name,
        value: parsed === null ? claim.rawValue.trim() : String(parsed),
        unit: valueUnit(run, claim),
        date: valueDate(run, claim, fallbackDate),
        saved: claim.status === "accepted",
        route: routeFor(run, claim, semenRun, fromLabel),
      };
    });
}

/** How many values a run found that still wait for the patient. */
export function countDocumentValuesToReview(
  run: DocumentProcessingRun | null | undefined,
): number {
  if (!run) return 0;
  return findDocumentValues(run, null).filter((value) => !value.saved).length;
}

function sameNumber(left: string, right: string): boolean {
  const a = parseLocaleNumber(left);
  const b = parseLocaleNumber(right);
  return a !== null && b !== null ? a === b : left.trim() === right.trim();
}

function sameUnit(left: string, right: string): boolean {
  const normalize = (unit: string) =>
    unit.trim().toLowerCase().replace(/[µμ]/g, "u");
  return normalize(left) === normalize(right);
}

/**
 * What confirming a document's values sends: a review for every value still
 * waiting, the capabilities to promote, and the values to record by hand.
 *
 * - An untouched value the writer can file is accepted. Its normalized value
 *   carries the figure, unit, name and date the patient saw, so the
 *   observation is dated as the document is, not as the day it was read.
 * - A removed value is rejected.
 * - A corrected value, or one the writer cannot file as shown, is rejected
 *   and recorded as a patient-entered observation instead. The writers keep
 *   the claim's raw value, unit and label, so a correction accepted on the
 *   claim would still read as the original figure.
 * - On a semen analysis, the claims that make the report read as one (its
 *   section headings, collection date, sample number) are accepted with the
 *   figures, so the semen writer can file a spermiogram from them.
 */
export function planDocumentValueConfirmation({
  run,
  documentDate,
  edits,
  removedClaimIds,
  added,
}: {
  run: DocumentProcessingRun;
  documentDate: string | null;
  edits: Readonly<Record<string, DocumentValueDraft>>;
  removedClaimIds: ReadonlySet<string>;
  added: readonly DocumentValueDraft[];
}): DocumentValueConfirmationPlan {
  const claimsById = new Map(run.claims.map((claim) => [claim.id, claim]));
  const reviews: DocumentClaimReview[] = [];
  const record: DocumentValueDraft[] = [];
  const capabilities = new Set<DocumentValueCapability>();
  const accepted = new Set<string>();

  const values = findDocumentValues(run, documentDate).filter(
    (value) =>
      !value.saved &&
      REVIEWABLE_STATUSES.has(
        claimsById.get(value.claimId)?.status ?? "rejected",
      ),
  );

  for (const value of values) {
    const claim = claimsById.get(value.claimId);
    if (!claim) continue;
    const base = { claimId: claim.id, expectedVersion: claim.version };

    if (removedClaimIds.has(claim.id)) {
      reviews.push({
        ...base,
        decision: "reject",
        reason: "Removed by the patient before saving.",
      });
      continue;
    }

    const draft = edits[claim.id] ?? {
      name: value.name,
      value: value.value,
      unit: value.unit,
      date: value.date,
    };
    const figureChanged =
      draft.name.trim() !== value.name ||
      !sameNumber(draft.value, value.value) ||
      !sameUnit(draft.unit, value.unit);
    const dateChanged = (draft.date ?? null) !== (value.date ?? null);
    const filedAsShown =
      value.route === "clinical_result"
        ? !figureChanged
        : value.route === "semen_analysis"
          ? !figureChanged && !dateChanged
          : false;

    if (!filedAsShown) {
      reviews.push({
        ...base,
        decision: "reject",
        reason:
          figureChanged || dateChanged
            ? "Corrected by the patient and saved as a patient-entered value."
            : "Saved as a patient-entered value linked to the document.",
      });
      record.push(draft);
      continue;
    }

    reviews.push({
      ...base,
      decision: "accept",
      reason: "Confirmed by the patient.",
      normalizedValue: {
        ...(claim.normalizedValue ?? {}),
        name: draft.name.trim(),
        value: String(parseLocaleNumber(draft.value) ?? draft.value.trim()),
        unit: draft.unit.trim(),
        ...(draft.date ? { date: draft.date } : {}),
      },
    });
    accepted.add(claim.id);
    if (value.route !== "record") capabilities.add(value.route);
  }

  if (capabilities.has("semen_analysis")) {
    const reviewed = new Set(reviews.map((review) => review.claimId));
    for (const claim of run.claims) {
      if (reviewed.has(claim.id) || !REVIEWABLE_STATUSES.has(claim.status)) {
        continue;
      }
      const reportsSemen = SEMEN_DOCUMENT_NAME.test(
        `${claim.fieldPath} ${claim.rawLabel} ${claim.rawValue}`.toLowerCase(),
      );
      const isCompanion =
        (isSemenGroupClaim(claim) && !isMeasuredValueClaim(run, claim, true)) ||
        reportsSemen;
      if (!isCompanion || isReferenceBound(claim)) continue;
      reviews.push({
        claimId: claim.id,
        expectedVersion: claim.version,
        decision: "accept",
        reason: "Confirmed with the semen analysis it belongs to.",
      });
      accepted.add(claim.id);
    }
  }

  record.push(...added);

  return {
    reviews,
    capabilities: [...capabilities],
    acceptedClaimIds: [...accepted],
    record,
  };
}
